import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { useMasterData } from "../../../hooks/useMasterData";
import { Student, StudentStatus } from "../students/api";
import { studentStatusApi } from "./studentStatusApi";
import ChangeStudentStatusDialog from "./ChangeStudentStatusDialog";

const StudentStatusList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { masters, fetchMasters, fetchSections } = useMasterData();

  const [students, setStudents] = useState<Student[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState<StudentStatus | "">("");
  const [sessionId, setSessionId] = useState<string>("");
  const [classId, setClassId] = useState<string>("");
  const [sectionId, setSectionId] = useState<string>("");
  const [sections, setSections] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStudents, setDialogStudents] = useState<Student[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchMasters();
  }, [fetchMasters]);

  useEffect(() => {
    if (classId) {
      fetchSections(classId).then((secs) => setSections(secs || []));
    } else {
      setSections([]);
    }
  }, [classId, fetchSections]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const res = await studentStatusApi.getAll({
        page: page + 1,
        limit,
        status: status || undefined,
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        search: search || undefined,
      });
      setStudents(res.data);
      setTotal(res.meta.total);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    const res = await studentStatusApi.getSummary({
      sessionId: sessionId || undefined,
      classId: classId || undefined,
      sectionId: sectionId || undefined,
      search: search || undefined,
    });
    setSummary(res || {});
  };

  useEffect(() => {
    loadStudents();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, sessionId, classId, sectionId, search]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (studentsToEdit: Student[]) => {
    setDialogStudents(studentsToEdit);
    setDialogOpen(true);
  };

  const handleToggleRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleToggleAll = () => {
    const currentIds = students.map((s) => s.id);
    if (currentIds.length === 0) {
      return;
    }
    const allSelected = currentIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !currentIds.includes(id)));
    } else {
      setSelectedIds(Array.from(new Set([...selectedIds, ...currentIds])));
    }
  };

  const selectedCount = selectedIds.length;

  const selectedStudentsForDialog = useMemo(
    () => students.filter((s) => selectedIds.includes(s.id)),
    [students, selectedIds],
  );

  const handleExport = async () => {
    if (total === 0) return;
    setExporting(true);
    try {
      const res = await studentStatusApi.getAll({
        page: 1,
        limit: total,
        status: status || undefined,
        sessionId: sessionId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        search: search || undefined,
      });
      const rows = res.data || [];

      const header = [
        "Admission No",
        "Scholar No",
        "Student Name",
        "Class",
        "Section",
        "Current Status",
        "Admission Date",
      ];

      const dataRows = rows.map((s) => [
        s.admissionNumber || "",
        s.scholarNumber || "",
        `${s.firstName} ${s.lastName || ""}`.trim(),
        s.class?.name || "",
        s.section?.name || "",
        s.status || "",
        s.admissionDate
          ? new Date(s.admissionDate).toLocaleDateString()
          : "",
      ]);

      const lines = [header.join(",")];
      dataRows.forEach((row) => {
        const line = row
          .map((value) => {
            const v = value || "";
            if (v.includes(",") || v.includes('"')) {
              return `"${v.replace(/"/g, '""')}"`;
            }
            return v;
          })
          .join(",");
        lines.push(line);
      });

      const csvContent = lines.join("\r\n");
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "student-status.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const totalStudents = summary.TOTAL || 0;
  const activeCount = summary.ACTIVE || 0;
  const inactiveCount = summary.INACTIVE || 0;
  const passedOutCount = summary.PASSED_OUT || 0;
  const tcCount = summary.TC || 0;
  const reStudyCount = summary.RE_STUDY || 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5">Active / Deactive Students</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {hasPermission("STUDENT_STATUS_UPDATE") && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              disabled={selectedCount === 0}
              onClick={() => handleOpenDialog(selectedStudentsForDialog)}
            >
              Change Status
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            disabled={exporting || total === 0}
          >
            Export
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Paper sx={{ p: 2 }}>
            <Typography color="textSecondary" variant="caption">
              Total Students
            </Typography>
            <Typography variant="h6">{totalStudents}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Paper sx={{ p: 2 }}>
            <Typography color="textSecondary" variant="caption">
              Active Students
            </Typography>
            <Typography variant="h6">{activeCount}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Paper sx={{ p: 2 }}>
            <Typography color="textSecondary" variant="caption">
              Inactive Students
            </Typography>
            <Typography variant="h6">{inactiveCount}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Paper sx={{ p: 2 }}>
            <Typography color="textSecondary" variant="caption">
              Passed Out
            </Typography>
            <Typography variant="h6">{passedOutCount}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Paper sx={{ p: 2 }}>
            <Typography color="textSecondary" variant="caption">
              TC / Re-Study
            </Typography>
            <Typography variant="h6">{tcCount + reStudyCount}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as StudentStatus | "")
              }
              fullWidth
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={StudentStatus.ACTIVE}>ACTIVE</MenuItem>
              <MenuItem value={StudentStatus.INACTIVE}>INACTIVE</MenuItem>
              <MenuItem value={StudentStatus.PASSED_OUT}>PASSED OUT</MenuItem>
              <MenuItem value={StudentStatus.TC}>TC</MenuItem>
              <MenuItem value={StudentStatus.RE_STUDY}>RE-STUDY</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              label="Session"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {(masters.academicSessions || []).map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              label="Class"
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {(masters.classes || []).map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              label="Section"
              value={sectionId}
              onChange={(e) => setSectionId(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {sections.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Student"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < students.length
                    }
                    checked={
                      students.length > 0 &&
                      students.every((s) => selectedIds.includes(s.id))
                    }
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell>Admission No</TableCell>
                <TableCell>Scholar No</TableCell>
                <TableCell>Student Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Current Status</TableCell>
                <TableCell>Admission Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(s.id)}
                      onChange={() => handleToggleRow(s.id)}
                    />
                  </TableCell>
                  <TableCell>{s.admissionNumber}</TableCell>
                  <TableCell>{s.scholarNumber || "-"}</TableCell>
                  <TableCell>
                    {s.firstName} {s.lastName}
                  </TableCell>
                  <TableCell>{s.class?.name || "-"}</TableCell>
                  <TableCell>{s.section?.name || "-"}</TableCell>
                  <TableCell>
                    <Chip label={s.status} size="small" />
                  </TableCell>
                  <TableCell>
                    {s.admissionDate
                      ? new Date(s.admissionDate).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/scholar-register/students/${s.id}`)
                      }
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {hasPermission("STUDENT_STATUS_UPDATE") && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog([s])}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {students.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No students found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <ChangeStudentStatusDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        students={dialogStudents}
        onUpdated={() => {
          setDialogOpen(false);
          setSelectedIds([]);
          loadStudents();
          loadSummary();
        }}
      />
    </Container>
  );
};

export default StudentStatusList;
